import { useState } from 'react';
import { cn } from '@ais-ui/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { baseColours } from '@ais-ui/react-openlayers';

interface ColorSwatchProps {
  color: string;
  onChange: (color: keyof typeof baseColours) => void;
  className?: string;
}

const colorEntries = Object.entries(baseColours) as [
  keyof typeof baseColours,
  string,
][];

/**
 * A clickable color swatch that opens a popover color picker.
 * Used for child layers in the nested layer control.
 */
export const ColorSwatch = ({
  color,
  onChange,
  className,
}: ColorSwatchProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'size-4 rounded-sm border border-border/50 flex-shrink-0 cursor-pointer',
                'hover:ring-2 hover:ring-primary/30 transition-shadow',
                className,
              )}
              style={{ backgroundColor: color }}
              onClick={(e) => e.stopPropagation()}
            />
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Change color</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto p-2"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid grid-cols-5 gap-1.5">
          {colorEntries.map(([name, hsl]) => (
            <button
              key={name}
              className={cn(
                'size-6 rounded-sm border border-border/50 cursor-pointer',
                'hover:ring-2 hover:ring-primary/50 transition-shadow',
                color === hsl && 'ring-2 ring-primary',
              )}
              style={{ backgroundColor: hsl }}
              title={name}
              onClick={() => {
                onChange(name);
                setOpen(false);
              }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
